import React from "react";
import { Plus } from "lucide-react";
import { METRIC } from "./supernovaSummary";
import { SLOT_KIND } from "./supernovaSlots";
import { fmtCount, plural } from "./supernovaFormat";

/**
 * "Strategy slots · lit by your stake" — one cell per slot the member's stake
 * has unlocked, labelled with whatever is sitting in it.
 *
 *   USED      an automated strategy from the Strategy Automation engine
 *   OPEN      backed by a stake, nothing allocated to it yet
 *   UNBACKED  a strategy still allocated after the stake behind it left
 *   UNKNOWN   the slot exists (the stake says so) but usage isn't connected
 *   ghost     "Stake +1" — an action, not data; opens the stake modal
 *
 * THE RAIL NEVER GUESSES. Both halves of the slot rule can be missing
 * independently (see supernovaSlots.js), and each missing half changes what the
 * caption is allowed to say. With no staked count we do not draw open slots,
 * because "Open" is a claim that a stake is backing it. With no usage we draw
 * the slots but leave them unlabelled rather than calling them free.
 *
 * PAUSE IS A DISPLAY STATE. An unbacked cell describes an allocation the stake
 * no longer covers. The engine does not enforce the slot gate yet, and no copy
 * on this surface may imply that it does.
 *
 * COMPLIANCE: a count of automation seats. No return, yield, price, projection,
 * or dollar figure appears here, and none may be added.
 */

/* The mockup's `.slot` tile, one class per state (supernova.css). */
const CELL_KIND = {
  [SLOT_KIND.USED]: "sn-slot-used",
  [SLOT_KIND.OPEN]: "sn-slot-open",
  [SLOT_KIND.UNBACKED]: "sn-slot-unbacked",
  [SLOT_KIND.UNKNOWN]: "sn-slot-unknown",
};

/* What a screen reader hears instead of a three-letter ticker. */
const cellDescription = (cell, index) => {
  const position = `Slot ${index + 1}`;
  switch (cell.kind) {
    case SLOT_KIND.USED:
      return cell.strategy
        ? `${position}: ${cell.strategy} automated`
        : `${position}: in use`;
    case SLOT_KIND.OPEN:
      return `${position}: open, nothing allocated to it`;
    case SLOT_KIND.UNBACKED:
      return cell.strategy
        ? `${position}: ${cell.strategy} allocated with no stake behind it`
        : `${position}: allocated with no stake behind it`;
    default:
      return `${position}: unlocked by your stake, usage not connected`;
  }
};

const SlotCell = ({ cell, index }) => {
  const description = cellDescription(cell, index);
  return (
    <div
      role="listitem"
      title={description}
      className={`sn-slot ${CELL_KIND[cell.kind] || CELL_KIND[SLOT_KIND.UNKNOWN]}`}
    >
      <span aria-hidden="true">{cell.label}</span>
      <span className="sr-only">{description}</span>
    </div>
  );
};

/**
 * The ghost cell. An action — it opens the stake modal, it displays nothing.
 * Deliberately NOT a listitem: it is not one of the member's slots.
 *
 * Its accessible name carries the visible label plus the context a three-word
 * cell can't: which slot this is, and why it's unavailable when it is.
 */
const StakeGhostCell = ({ onStake, reason }) => (
  <button
    type="button"
    onClick={onStake || undefined}
    disabled={!onStake}
    title={reason || "Stake another Supernova to open the next slot"}
    aria-label={`Open a new slot — Stake +1 Supernova.${reason ? ` ${reason}` : ""}`}
    className="sn-slot sn-slot-open sn-slot-ghost"
  >
    <Plus size={13} aria-hidden="true" className="mr-1" />
    Stake +1
  </button>
);

/**
 * The one line under the rail. Every branch states only what the two halves
 * actually support — see the module comment.
 */
const RailCaption = ({ slots, onOpenAutomation }) => {
  const { stakedKnown, usageKnown, staked, used, free, unbacked } = slots;

  if (!stakedKnown && !usageKnown) {
    return (
      <>
        Each Supernova you stake lights one strategy slot. We can&apos;t show
        your slots yet — neither your staked count nor your slot usage is
        connected here.
      </>
    );
  }

  if (!stakedKnown) {
    return (
      <>
        {plural(used, "strategy is", "strategies are")} automated. Your staked
        count isn&apos;t connected yet, so we can&apos;t show which of these
        slots your stake is backing.
      </>
    );
  }

  if (!usageKnown) {
    return (
      <>
        {plural(staked, "slot", "slots")} unlocked by your stake. Slot usage
        lives in Strategy Automation and isn&apos;t connected here yet, so we
        can&apos;t say which are free.
      </>
    );
  }

  if (unbacked > 0) {
    return (
      <>
        {fmtCount(used)} allocated but only {plural(staked, "staked", "staked")} —{" "}
        {unbacked === 1 ? "the newest strategy shows" : `the ${fmtCount(unbacked)} newest strategies show`}{" "}
        as unbacked until you stake again or clear{" "}
        {unbacked === 1 ? "its" : "their"} allocation.{" "}
        <span className="text-arc-ink-4">
          Display only — the engine doesn&apos;t enforce the slot gate yet.
        </span>
      </>
    );
  }

  return (
    <>
      {fmtCount(used)} of {plural(staked, "slot", "slots")} running strategies
      {free > 0 ? (
        <>
          {" · "}
          {fmtCount(free)} open —{" "}
          {onOpenAutomation ? (
            <button
              type="button"
              onClick={onOpenAutomation}
              className="underline font-semibold text-arc-accent-ink"
            >
              allocate on the Strategy Automation tab
            </button>
          ) : (
            "allocate on the Strategy Automation tab"
          )}
        </>
      ) : (
        " · stake 1 more to open the next"
      )}
    </>
  );
};

/**
 * @param {Object} props
 * @param {Object} props.slots            deriveSlots() result
 * @param {string} [props.stakedStatus]   METRIC status of the staked count
 * @param {string} [props.stakedNote]     why the staked count is missing
 * @param {string} [props.usageNote]      why slot usage is missing
 * @param {(() => void)|null} [props.onStake]  opens the stake modal
 * @param {string} [props.stakeReason]    why staking isn't offered right now
 * @param {(() => void)|null} [props.onOpenAutomation]  jump to Strategy Automation
 */
const SupernovaSlotRail = ({
  slots,
  stakedStatus,
  stakedNote,
  usageNote,
  onStake,
  stakeReason,
  onOpenAutomation,
}) => {
  const showStakedNote = !slots.stakedKnown && stakedStatus !== METRIC.LOADING && stakedNote;

  return (
    <div data-testid="supernova-slot-rail">
      <div role="list" aria-label="Strategy slots" className="sn-rail">
        {slots.cells.map((cell, index) => (
          <SlotCell key={cell.key} cell={cell} index={index} />
        ))}
        <StakeGhostCell onStake={onStake} reason={stakeReason} />
      </div>

      <p className="text-[13px] text-arc-ink-3 mt-4 leading-[1.6]">
        <RailCaption slots={slots} onOpenAutomation={onOpenAutomation} />
      </p>

      {(showStakedNote || (!slots.usageKnown && usageNote)) && (
        <p className="text-caption text-arc-ink-4 mt-1.5">
          {/* Both halves can be missing for the same reason — say it once. */}
          {Array.from(
            new Set(
              [showStakedNote ? stakedNote : null, !slots.usageKnown ? usageNote : null].filter(
                Boolean
              )
            )
          ).join(" ")}
        </p>
      )}
    </div>
  );
};

export default SupernovaSlotRail;
